import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, ImagePlus, Save } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { HERO_FADE } from "@/components/HeroBanner";

const label = "text-xs font-semibold text-slate-500 mb-1 block";

function LinesEditor({ lines, onChange }) {
  const set = (i, k, v) => onChange(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="color" data-testid={`hero-line-color-${i}`} value={l.color} onChange={(e) => set(i, "color", e.target.value)} className="w-10 h-10 rounded-lg border border-slate-200 shrink-0" />
          <Input data-testid={`hero-line-word-${i}`} value={l.word} onChange={(e) => set(i, "word", e.target.value)} className="rounded-xl h-10" style={{ color: l.color }} />
          <button type="button" data-testid={`hero-line-del-${i}`} onClick={() => onChange(lines.filter((_, j) => j !== i))} className="p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="hero-line-add" onClick={() => onChange([...lines, { word: "", color: "#10B981" }])} className="text-sm font-semibold text-emerald-600 flex items-center gap-1"><Plus className="w-4 h-4" />Shto rresht</button>
    </div>
  );
}

function ImagesEditor({ images, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...(e.target.files || [])]; e.target.value = "";
    setBusy(true);
    try {
      const added = [];
      for (const f of files) { const fd = new FormData(); fd.append("file", f); const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); added.push({ url: r.data.url, city: "" }); }
      onChange([...images, ...added]);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {images.map((im, i) => (
          <div key={im.url + i} className="rounded-xl border border-slate-100 overflow-hidden bg-white">
            <div className="relative">
              <div className="bg-white"><img src={im.url} alt="" className="w-full h-20 object-cover object-right" style={HERO_FADE} /></div>
              <button type="button" data-testid={`hero-img-del-${i}`} onClick={() => onChange(images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <input data-testid={`hero-img-city-${i}`} value={im.city} placeholder="Qyteti" onChange={(e) => onChange(images.map((x, j) => (j === i ? { ...x, city: e.target.value } : x)))} className="w-full px-2 py-1.5 text-xs outline-none" />
          </div>
        ))}
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="hero-img-input" />
      <button type="button" data-testid="hero-img-add" disabled={busy} onClick={() => ref.current?.click()} className="mt-2 w-full h-11 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-600 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
        <ImagePlus className="w-4 h-4" />{busy ? "Duke ngarkuar..." : "Shto foto"}
      </button>
    </div>
  );
}

export default function AdminHero() {
  const [h, setH] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/hero").then((r) => setH(r.data)); }, []);
  if (!h) return <p className="text-center text-slate-400 py-6 text-sm">Duke ngarkuar...</p>;
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/hero", h); setH(r.data); toast.success("Kreu i faqes u ruajt"); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="admin-hero">
      <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
        <div><label className={label}>Fjala e parë</label><Input data-testid="hero-prefix" value={h.prefix} onChange={(e) => setH({ ...h, prefix: e.target.value })} className="rounded-xl h-10" /></div>
        <div><label className={label}>Rreshtat që shkruhen (makinë shkrimi)</label><LinesEditor lines={h.lines} onChange={(lines) => setH({ ...h, lines })} /></div>
        <div><label className={label}>Teksti poshtë</label><Input data-testid="hero-tagline-input" value={h.tagline} onChange={(e) => setH({ ...h, tagline: e.target.value })} className="rounded-xl h-10" /></div>
        <div><label className={label}>Teksti në fushën e kërkimit</label><Input data-testid="hero-placeholder-input" value={h.placeholder} onChange={(e) => setH({ ...h, placeholder: e.target.value })} className="rounded-xl h-10" /></div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <label className={label}>Fotot ({h.images.length}) · ndërrohen rastësisht për çdo vizitor. Ana e majtë bëhet e bardhë automatikisht.</label>
        <div className="flex items-center gap-2 mb-3 text-sm text-slate-700" data-testid="hero-interval">
          Ndërro foton çdo
          <select data-testid="hero-interval-select" value={String(h.interval_hours ?? 2)} onChange={(e) => setH({ ...h, interval_hours: Number(e.target.value) })} className="h-10 rounded-xl border border-slate-200 bg-white px-2 text-sm font-semibold">
            {[[0.25, "15 minuta"], [0.5, "30 minuta"], [1, "1 orë"], [2, "2 orë"], [3, "3 orë"], [4, "4 orë"], [6, "6 orë"], [8, "8 orë"], [12, "12 orë"], [24, "1 ditë"], [48, "2 ditë"], [168, "1 javë"]].map(([v, t]) => <option key={v} value={String(v)}>{t}</option>)}
          </select>
        </div>
        <ImagesEditor images={h.images} onChange={(images) => setH({ ...h, images })} />
      </div>
      <Button data-testid="hero-save" onClick={save} disabled={saving} className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600"><Save className="w-4 h-4 mr-1" />{saving ? "Duke ruajtur..." : "Ruaj kreun e faqes"}</Button>
    </div>
  );
}
